import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Loader2, GitBranch, Globe, Building2, Check, ChevronDown } from 'lucide-react';

export function BranchSwitcher() {
  const { user, switchBranch } = useAuth();
  const [branches, setBranches] = useState([]);
  const [switching, setSwitching] = useState(false);

  const canSwitch = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'finance';
  const selectedId = user?.selected_tenant_id;
  const selectedBranch = branches.find(branch => branch.tenant_id === selectedId);
  const selectedLabel = selectedId
    ? selectedBranch?.name || (selectedId === user?.tenant_id ? user?.tenant_name : null) || 'Selected branch unavailable'
    : 'All Branches';

  useEffect(() => {
    if (!canSwitch) return;
    let active = true;
    const fetchBranches = async () => {
      try {
        const res = await api.get('/tenants', { timeout: 10000 });
        if (active) setBranches((res.data.tenants || []).filter(t => t.is_active !== false));
      } catch (e) { console.warn('Branch list unavailable:', e); }
    };
    fetchBranches();
    window.addEventListener('online', fetchBranches);
    return () => { active = false; window.removeEventListener('online', fetchBranches); };
  }, [canSwitch, user?.user_id]);

  if (!canSwitch) return <StaffBranchSwitcher />;
  if (branches.length === 0) return null;

  const handleSwitch = async (branchId, branchName) => {
    setSwitching(true);
    const ok = await switchBranch(branchId, branchName);
    if (ok) {
      toast.success(branchId === 'all' ? 'Viewing all branches' : `Switched to ${branchName}`);
    } else {
      toast.error('Failed to switch branch');
    }
    setSwitching(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-8 text-xs max-w-[140px] sm:max-w-[200px]" disabled={switching} title={selectedLabel} data-testid="branch-switcher">
          {switching ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitBranch className="h-3 w-3" />}
          <span className="truncate" data-testid="branch-selected-label">{selectedLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="branch-switcher-menu">
        <DropdownMenuItem onClick={() => handleSwitch('all', null)} className="gap-2" data-testid="branch-option-all">
          <Globe className="h-4 w-4" />
          <span className="flex-1">All Branches</span>
          {!selectedId && <Check className="h-3.5 w-3.5 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {branches.map(b => (
          <DropdownMenuItem key={b.tenant_id} onClick={() => handleSwitch(b.tenant_id, b.name)} className="gap-2" data-testid={`branch-option-${b.tenant_id}`}>
            <Building2 className="h-4 w-4" />
            <span className="flex-1 truncate">{b.name}</span>
            {selectedId === b.tenant_id && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Staff granted extra branches by the owner work in one branch at a time.
function StaffBranchSwitcher() {
  const [access, setAccess] = useState(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let active = true;
    const refresh = () => api.get('/branches/my-access', { timeout: 10000 }).then(r => { if (active) setAccess(r.data); }).catch(() => {});
    refresh();
    window.addEventListener('online', refresh);
    return () => { active = false; window.removeEventListener('online', refresh); };
  }, []);

  if (!access || access.branches.length < 2) return null;
  const current = access.branches.find(b => b.tenant_id === access.current);

  const go = async (branch) => {
    setSwitching(true);
    try {
      await api.post('/branches/switch', { tenant_id: branch.tenant_id });
      toast.success(`Switched to ${branch.name}`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to switch branch');
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-8 text-xs max-w-[160px] sm:max-w-[220px]" disabled={switching} data-testid="staff-branch-switcher">
          {switching ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitBranch className="h-3 w-3" />}
          <span className="truncate" data-testid="staff-branch-current">{current?.name || 'My branch'}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="staff-branch-switcher-menu">
        {access.branches.map(b => (
          <DropdownMenuItem key={b.tenant_id} onClick={() => go(b)} disabled={b.tenant_id === access.current} className="gap-2" data-testid={`staff-branch-option-${b.tenant_id}`}>
            <Building2 className="h-4 w-4" />
            <span className="flex-1 truncate" title={b.name}>{b.name}{b.is_home ? ' (home)' : ''}</span>
            {b.tenant_id === access.current && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
